import { DatePipe } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@core/services/auth/auth.service';
import { ClientService } from '@core/services/client/client.service';
import { Role } from '@core/services/role/role.model';
import { RoleService } from '@core/services/role/role.service';
import { EmailErrorMessage, PasswordErrorMessage, ProvinceModel, UserErrorMessage, UserModel, UserStatus } from '@core/services/user/user.model';
import { UserService } from '@core/services/user/user.service';
import { emailDomainValidator } from '@shared/directives/email-domain.directive';
import { emailFormatValidator } from '@shared/directives/email-format.directive';
import { inputNoWhitespaceValidator } from '@shared/directives/input-no-whitespace.directive';
import { phoneExtensionFormatValidator, phoneFormatValidator } from '@shared/directives/phone-format.directive';
import { UserEmailExistsValidator } from '@shared/directives/user-email-exists.directive';
import { NgxSpinnerService } from 'ngx-spinner';
import { ResetPasswordComponent } from '../reset-password/reset-password.component';
import { DescriptionModel } from '@core/services/base.model';

const PAGE_TITLE: string = 'Manage User';

@Component({
  selector: 'Teranet-UserEdit',
  templateUrl: './user-edit.component.html'
})
export class UserEditComponent implements OnInit {
  @Input() UserId: number;
  changePasswordDate: string;
  clientInfoDescriptions: DescriptionModel[] = [];
  createdDate: Date;
  emailErrorMessage = EmailErrorMessage;
  errorMessages: string[] = [];
  hasError: boolean = false;
  id: number = -1;
  isCopy: boolean = false;
  isEdit: boolean = false;
  isExistingEmail: boolean = false;
  isLocked: boolean = false;
  passwordErrorMessage = PasswordErrorMessage;
  provinces: ProvinceModel[] = [];
  languages: string[] = ['English', 'French'];
  lastLoginDate: string;
  pageTitle: string = PAGE_TITLE;
  policyAgreedDate: string;
  roles: Role[] = [];
  selectedRole: any;
  userStatuses(): Array<string> {
    return Object.values(UserStatus);
  }
  user: UserModel;
  userErrorMessage = UserErrorMessage;
  userForm: FormGroup;

  @ViewChild('email', { static: false }) emailRef: ElementRef;
  @ViewChild('ext', { static: false }) extRef: ElementRef;
  @ViewChild('firstName', { static: false }) firstNameRef: ElementRef;
  @ViewChild('lastName', { static: false }) lastNameRef: ElementRef;
  @ViewChild('middleName', { static: false }) middleNameRef: ElementRef;
  @ViewChild('password', { static: false }) passwordRef: ElementRef;
  @ViewChild('phone', { static: false }) phoneRef: ElementRef;
  @ViewChild('userName', { static: false }) userNameRef: ElementRef;

  constructor(
    private authService: AuthService,
    private clientService: ClientService,
    private datePipe: DatePipe,
    private userEmailExistsValidator: UserEmailExistsValidator,
    private roleService: RoleService,
    private route: ActivatedRoute,
    private router: Router,
    private spinner: NgxSpinnerService,
    private userService: UserService,
    public dialog: MatDialog,
  ) {
    this.userForm = new FormGroup({
      accountState: new FormControl(),
      clientInfoId: new FormControl('', Validators.required),
      email: new FormControl('', [emailFormatValidator, emailDomainValidator]),
      ext: new FormControl('', phoneExtensionFormatValidator),
      firstName: new FormControl('', [inputNoWhitespaceValidator, Validators.required, Validators.maxLength(100)]),
      forcePassword: new FormControl(1),
      id: new FormControl(0),
      language: new FormControl('', Validators.required),
      lastName: new FormControl('', [inputNoWhitespaceValidator, Validators.required, Validators.maxLength(140)]),
      lockUnlock: new FormControl(''),
      middleName: new FormControl('', [inputNoWhitespaceValidator, Validators.maxLength(100)]),
      phone: new FormControl('', phoneFormatValidator),
      password: new FormControl('', [
        Validators.required,
        Validators.pattern('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@%+!$&?()[\\]/\\\\]).{10,}')]),
      provinceId: new FormControl('', Validators.required),
      roleId: new FormControl('', Validators.required),
      status: new FormControl('Active'),
      userName: new FormControl('', {
        validators: [Validators.required, emailFormatValidator, emailDomainValidator],
        asyncValidators: [this.userEmailExistsValidator.validate.bind(this.userEmailExistsValidator)], updateOn: 'blur'
      }),
    });
  }

  ngOnInit() {
    this.spinner.show();

    this.userService.getProvinces().subscribe(value => {
      this.provinces = value as ProvinceModel[];
    });

    this.clientService.getClientInfoDescriptions().subscribe(value => {
      this.clientInfoDescriptions = value as DescriptionModel[];

      this.isCreateOrUpdate();

      if (!this.isEdit && !this.isCopy && this.clientInfoDescriptions.length === 1 && this.clientInfoDescriptions[0].id) {
        this.userForm.get('clientInfoId').setValue(this.clientInfoDescriptions[0].id);
        this.getCorporationRoles();
      }
    });
  }

  // Check errors upon submission and show all the errors on top
  checkError() {
    this.hasError = false;
    this.errorMessages = [];
    let firstControl: ElementRef<any> = null;

    if (this.userForm.get('userName') !== null) {
      if (this.userForm.get('userName').value.trim() === '') {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.userNameRef;
        }
        this.userForm.get('userName').markAsTouched();
        this.errorMessages.push(UserErrorMessage.USER_NAME_REQUIRED);
      } else if (this.userForm.get('userName').hasError('invalidEmail')) {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.userNameRef;
        }
        this.userForm.get('userName').markAsTouched();
        this.errorMessages.push(EmailErrorMessage.IS_NOT_EMAIL);
      } else if (this.userForm.get('userName').hasError('invalidDomain')) {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.userNameRef;
        }
        this.userForm.get('userName').markAsTouched();
        this.errorMessages.push(EmailErrorMessage.EMAIL_DOMAIN);
      } else if (!this.isEdit && this.userForm.get('userName').hasError('userEmailExists')) {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.userNameRef;
        }
        this.userForm.get('userName').markAsTouched();
        this.errorMessages.push(UserErrorMessage.USER_NAME_EXISTS);
      }
    }

    if (!this.isEdit && this.userForm.get('password') !== null) {
      if (this.userForm.get('password').value.trim() === '') {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.passwordRef;
        }
        this.userForm.get('password').markAsTouched();
        this.errorMessages.push(PasswordErrorMessage.PASSWORD_REQUIRED);
      } else if (this.userForm.get('password').hasError('pattern')) {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.passwordRef;
        }
        this.userForm.get('password').markAsTouched();
        this.errorMessages.push(PasswordErrorMessage.IS_NOT_PASSWORD);
      }
    }

    if (this.userForm.get('firstName') !== null) {
      if (this.userForm.get('firstName').value.trim() === '') {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.firstNameRef;
        }
        this.userForm.get('firstName').markAsTouched();
        this.errorMessages.push(UserErrorMessage.FIRST_NAME_REQUIRED);
      } else if (this.userForm.get('firstName').hasError('maxlength')) {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.firstNameRef;
        }
        this.userForm.get('firstName').markAsTouched();
        this.errorMessages.push(`${UserErrorMessage.FIRST_NAME_MAX_LENGTH}${this.userForm.get('firstName').errors.maxlength.requiredLength}`);
      }
    }

    if (this.userForm.get('middleName') !== null) {
      if (this.userForm.get('middleName').hasError('whitespace')) {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.middleNameRef;
        }
        this.userForm.get('middleName').markAsTouched();
        this.errorMessages.push(UserErrorMessage.MIDDLE_NAME_WHITESPACE);
      } else if (this.userForm.get('middleName').hasError('maxlength')) {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.middleNameRef;
        }
        this.userForm.get('middleName').markAsTouched();
        this.errorMessages.push(`${UserErrorMessage.MIDDLE_NAME_MAX_LENGTH}${this.userForm.get('middleName').errors.maxlength.requiredLength}`);
      }
    }

    if (this.userForm.get('lastName') !== null) {
      if (this.userForm.get('lastName').value.trim() === '') {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.lastNameRef;
        }
        this.userForm.get('lastName').markAsTouched();
        this.errorMessages.push(UserErrorMessage.LAST_NAME_REQUIRED);
      } else if (this.userForm.get('lastName').hasError('maxlength')) {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.lastNameRef;
        }
        this.userForm.get('lastName').markAsTouched();
        this.errorMessages.push(`${UserErrorMessage.LAST_NAME_MAX_LENGTH}${this.userForm.get('lastName').errors.maxlength.requiredLength}`);
      }
    }

    if (this.userForm.get('email') !== null) {
      if (this.userForm.get('email').value !== '') {
        if (this.userForm.get('email').hasError('invalidEmail')) {
          this.hasError = true;
          if (firstControl === null) {
            firstControl = this.emailRef;
          }
          this.userForm.get('email').markAsTouched();
          this.errorMessages.push(EmailErrorMessage.IS_NOT_EMAIL);
        } else if (this.userForm.get('email').hasError('invalidDomain')) {
          this.hasError = true;
          if (firstControl === null) {
            firstControl = this.emailRef;
          }
          this.userForm.get('email').markAsTouched();
          this.errorMessages.push(EmailErrorMessage.EMAIL_DOMAIN);
        }
      }
    }

    if (this.userForm.get('phone') !== null) {
      if (this.userForm.get('phone').value !== '') {
        if (this.userForm.get('phone').hasError('invalidPhone')) {
          this.hasError = true;
          if (firstControl === null) {
            firstControl = this.phoneRef;
          }
          this.userForm.get('phone').markAsTouched();
          this.errorMessages.push(UserErrorMessage.IS_NOT_PHONE_NUMBER);
        }
      }
    }

    if (this.userForm.get('ext') !== null) {
      if (this.userForm.get('ext').value !== '') {
        if (this.userForm.get('ext').hasError('invalidExtension')) {
          this.hasError = true;
          if (firstControl === null) {
            firstControl = this.extRef;
          }
          this.userForm.get('ext').markAsTouched();
          this.errorMessages.push(UserErrorMessage.IS_NOT_PHONE_EXTENSION);
        }
      }
    }

    if (this.userForm.get('language') !== null) {
      if (
        this.userForm.get('language').value === null ||
        this.userForm.get('language').value === ''
      ) {
        this.hasError = true;
        this.userForm.get('language').markAsTouched();
        this.errorMessages.push(UserErrorMessage.LANGUAGE_REQUIRED);
      }
    }

    if (this.userForm.get('provinceId') !== null) {
      if (
        this.userForm.get('provinceId').value === null ||
        this.userForm.get('provinceId').value === ''
      ) {
        this.hasError = true;
        this.userForm.get('provinceId').markAsTouched();
        this.errorMessages.push(UserErrorMessage.PROVINCE_REQUIRED);
      }
    }

    if (this.userForm.get('clientInfoId') !== null) {
      if (
        this.userForm.get('clientInfoId').value === null ||
        typeof this.userForm.get('clientInfoId').value !== 'number'
      ) {
        this.hasError = true;
        this.userForm.get('clientInfoId').markAsTouched();
        this.errorMessages.push(UserErrorMessage.CORPORATION_REQUIRED);
      }
    }

    if (this.userForm.get('roleId') !== null) {
      if (
        this.userForm.get('roleId').value === null ||
        this.userForm.get('roleId').value === '' ||
        this.userForm.get('roleId').value === '-1'
      ) {
        this.hasError = true;
        this.userForm.get('roleId').markAsTouched();
        this.errorMessages.push(UserErrorMessage.ROLE_REQUIRED);
      }
    }

    // Focus the Error Div
    if (this.hasError) {
      if (firstControl !== null && firstControl.nativeElement !== null) {
        firstControl.nativeElement.focus();
      }
    }
  }

  copyUser(id: number) {
    this.userService.copyUser(id).subscribe(value => {
      if (value) {
        this.user = value as UserModel;
        this.refreshUser();
        this.spinner.hide();
      }
      else {
        this.spinner.hide();
        this.goToUser();
      }
    })
  }

  createOrUpdateUser() {
    this.spinner.show();
    this.checkError();

    if (this.userForm.valid && !this.hasError) {
      const updatedUser: UserModel = {
        clientInfoId: this.userForm.get('clientInfoId').value,
        email: this.userForm.get('email').value?.trim(),
        firstName: this.userForm.get('firstName').value.trim(),
        forcePassword: this.userForm.get('forcePassword').value ? true : false,
        id: this.id,
        isDeleted: false,
        language: this.userForm.get('language').value,
        lastName: this.userForm.get('lastName').value.trim(),
        lastUpdatedBy: this.authService.currentUserValue.username,
        middleName: this.userForm.get('middleName').value?.trim(),
        password: this.isEdit ? '' : this.userForm.get('password').value,
        phone: this.userForm.get('phone').value?.trim() + '.' + this.userForm.get('ext').value?.trim(),
        provinceId: this.userForm.get('provinceId').value,
        roleId: this.userForm.get('roleId').value,
        status: this.userForm.get('status').value,
        userName: this.userForm.get('userName').value.trim(),
      };

      if (this.id == -1) {
        // Create new User
        this.userService.createUser(updatedUser).subscribe(() => { })
      } else {
        // Update User
        this.userService.updateUser(updatedUser).subscribe(() => { })
      }

      this.spinner.hide();
      this.goToUser();
    }

    this.spinner.hide();
  }

  getCorporationRoles() {
    this.roleService.getRolesByClientInfoId(this.userForm.get('clientInfoId').value).subscribe(value => {
      this.roles = value as Role[];
    });
  }

  getUser() {
    if (this.id > 0) {
      this.userService.getUser(this.id).subscribe(value => {
        if (value) {
          this.user = value as UserModel;
          this.refreshUser();
          this.spinner.hide();
        }
        else {
          this.spinner.hide();
          this.goToUser();
        }
      });
    };
  }

  goToUser() {
    this.router.navigateByUrl('/user');
  }

  isCreateOrUpdate() {
    this.route.queryParams.subscribe(params => {
      const userName = this.userForm.get('userName');
      const password = this.userForm.get('password');
      const clientInfoId = this.userForm.get('clientInfoId');

      if (params['id']) {
        // It means this is a edit user page
        // Reset Form Validataions and hide unnessary fields
        // Fillout the form based on the data
        this.isEdit = true;
        this.id = parseInt(params['id']);

        // Set User Name Validation
        userName.asyncValidator = null;

        // Hide Password and mark it not required
        password.clearValidators();
        password.updateValueAndValidity({ emitEvent: false });

        // Hide Corporation and mark it not required
        clientInfoId.clearValidators();
        clientInfoId.updateValueAndValidity({ emitEvent: false });

        // Set form value
        this.userForm.get('id').setValue(this.id);
        // Get values from DB and update the forms
        this.getUser();
      }
      else {
        // Otherwise it's a create new user page
        this.isEdit = false;

        // Set password validation
        password.setValidators([
          Validators.required,
          Validators.pattern('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@%+!$&?()[\\]/\\\\]).{10,}'),
        ]);
        password.updateValueAndValidity({ emitEvent: false });

        // Set Corporation validation
        clientInfoId.setValidators([Validators.required]);
        clientInfoId.updateValueAndValidity({ emitEvent: false });

        if (params['copy']) {
          // This means this is a copy user page
          // Fillout the form based on the data
          this.isCopy = true;
          this.copyUser(parseInt(params['copy']));
        }
        else {
          this.userForm.get('language').setValue('English');

          this.spinner.hide();
        }
      }
    });
  }

  refreshUser() {
    if (this.user != null) {
      // Set form values
      if (this.user.userName && this.user.userName.length > 0) {
        this.userForm.get('userName').setValue(this.user.userName);
      }

      if (this.user.firstName && this.user.firstName.length > 0) {
        this.userForm.get('firstName').setValue(this.user.firstName);
      }

      if (this.user.middleName && this.user.middleName.length > 0) {
        this.userForm.get('middleName').setValue(this.user.middleName);
      }

      if (this.user.lastName && this.user.lastName.length > 0) {
        this.userForm.get('lastName').setValue(this.user.lastName);
      }

      if (this.user.email && this.user.email.length > 0) {
        this.userForm.get('email').setValue(this.user.email);
      }

      if (this.user.phone && this.user.phone.length > 0 && this.user.phone.indexOf('.') !== -1) {
        this.userForm.get('phone').setValue(this.user.phone.split('.')[0] !== null ? this.user.phone.split('.')[0].trim() : '');
        this.userForm.get('ext').setValue(this.user.phone.split('.')[1] !== null ? this.user.phone.split('.')[1].trim() : '');
      }

      if (this.user.language && this.user.language.length > 0) {
        this.userForm.get('language').setValue(this.user.language);
      }

      if (this.user.provinceId > 0) {
        this.userForm.get('provinceId').setValue(this.user.provinceId);
      }

      if (this.user.clientInfoId >= 0) {
        this.userForm.get('clientInfoId').setValue(this.user.clientInfoId);
        this.getCorporationRoles();
      }

      if (this.user.roleId >= 0) {
        this.userForm.get('roleId').setValue(this.user.roleId);
      }

      if (this.user.status && this.user.status.length > 0) {
        this.userForm.get('status').setValue(this.user.status);
      }

      if (this.user.isLocked !== null) {
        this.isLocked = this.user.isLocked;
      }

      if (this.user.forcePassword !== null && !this.isCopy) {
        this.userForm.get('forcePassword').setValue(this.user.forcePassword);
      }

      if (this.user.changePasswordDateUtc) {
        this.changePasswordDate = this.toLocalTime(this.user.changePasswordDateUtc);
      }

      if (this.user.createdDate && !this.isCopy) {
        this.createdDate = this.user.createdDate;
      }

      if (this.user.lastLoginDateUtc) {
        this.lastLoginDate = this.toLocalTime(this.user.lastLoginDateUtc);
      }

      if (this.user.policyAgreedDateUtc) {
        this.policyAgreedDate = this.toLocalTime(this.user.policyAgreedDateUtc);
      }
      // Update title
      this.pageTitle = `Manage User | ${(this.userForm.get('firstName').value ?? '')} ${(this.userForm.get('lastName').value ?? '')}`;
    }
  }

  resetPassword() {
    const dialogRef = this.dialog.open(ResetPasswordComponent, {
      disableClose: true,
      maxWidth: '80vw',
      maxHeight: 'calc(100vh - 90px)',
      data: {
        id: this.userForm.get('id') != null ? this.userForm.get('id').value : -1,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      this.spinner.hide();
      if (result != undefined && result == true) {
        if (this.userForm.get('forcePassword') !== null
          && this.userForm.get('forcePassword').value !== null
          && this.userForm.get('forcePassword').value === false
        ) {
          this.userForm.get('forcePassword').setValue(true);
        }
      }
    });
  }

  roleSelected(event) {
    let target = event.source.selected._element.nativeElement;
    this.selectedRole = {
      value: event.value,
      text: target.innerText.trim(),
    };
  }

  toLocalTime(date: Date) {
    if (date) {
      let newDate = this.datePipe.transform(date, 'yyyy-MM-dd HH:mm:ss');
      let utcDate = this.datePipe.transform(new Date(newDate + ' UTC'), 'yyyy-MM-dd HH:mm:ss');
      return utcDate;
    } else {
      return '';
    }
  }

  unlockUser() {
    if (this.id > 0) {
      this.spinner.show();
      setTimeout(() => {
        this.userService.unlockUser(this.id).subscribe(value => {
          if (value) {
            this.isLocked = false;
            this.spinner.hide();
          }
        });
      }, 2000);
    }
  }

  updateEmailAddress() {
    if (this.userForm.get('userName')
      && !this.userForm.get('userName').hasError('pattern')
      && !this.isExistingEmail
      && this.userForm.get('email') !== null
      && this.userForm.get('email').value === ''
    ) {
      this.userForm.get('email').setValue(this.userForm.get('userName').value);
    }
  }
}
